/* Bulk binding edits: what ran, what it changed, and what an undo would revert.
 *
 * D-04, rebuilt to the reference design. The backend has been complete since K14's first consumer
 * landed; every control here is wired to the real routes — the runs listing, the journal preview,
 * and the 202 undo. Nothing is mocked and nothing new was invented for the visuals.
 *
 * **The screen's whole job is one distinction: undo reverts what the apply *did*, not what it
 * intended.** A binding already in the requested state before the run is `skipped`, and the undo
 * does not touch it — reverting it would be a change nobody asked for against a state that predates
 * the operation. So the revert button says how many rows it will actually move, taken from
 * `applied` alone, and the count breakdown sits beside it.
 *
 * **No apply control, deliberately, and the reference design agrees** — its callout is the first
 * thing on the page. An apply is submitted with the binding ids it is to change, and choosing those
 * is a decision made where the bindings are: the rules screen or `lumiq-cli`. A button here would
 * need a binding picker, which is a second rules screen wearing a different heading.
 *
 * **The preview is fetched on selection with no confirmation, because it writes nothing.** It is a
 * GET, journalled nowhere, reads no customer source, and is authorized as a read — the contract
 * states the reason: requiring `update` to see what an edit would do is how somebody runs the edit
 * to find out.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { BulkStarter } from './BulkStarter';

import { reportSource, useBulkReport, useBulkRuns, useUndoBulkRun } from '../api/bulk';
import {
  STATE_TONE,
  accountsForEveryItem,
  actionLabel,
  chunkRows,
  countsOf,
  revertStanding,
  revertableCount,
  undoReach,
} from '../model/presentation';
import type { ChunkRow } from '../model/presentation';
import type { BulkReport, BulkRun } from '@/shared/api/generated/rules';

export function BulkRunsScreen(): ReactElement {
  const { t } = useTranslation();
  const runs = useBulkRuns();
  const [selected, setSelected] = useState<string | null>(null);
  const items = runs.data ?? [];
  const current = items.find((run) => run.run_id === selected) ?? null;
  const report = useBulkReport(current);
  const undo = useUndoBulkRun();

  return (
    <div className="lq-bk lq-refscreen">
      <PageHeader
        title={t('bulk.title')}
        lede={t('bulk.intro')}
        meta={
          runs.isSuccess ? (
            <>
              <div>{t('bulk.meta.count', { count: items.length })}</div>
              <div>{t('bulk.meta.revertable', { count: revertableCount(items) })}</div>
            </>
          ) : undefined
        }
      />
      <div className="lq-pagerule" />

      {/* First, because it is the act the rest of the page is the record of. It replaced the
          "runs are started elsewhere" callout, which was true until this existed. */}
      <BulkStarter />

      {/* The accepted revert, as a strip above the table rather than inside the report — the
          report's counts still describe the apply, and the state that changes is in the table. */}
      {undo.isSuccess && (
        <div className="lq-bk__strip" role="status">
          <strong>{t('bulk.undo.acceptedTitle')}</strong>
          <span>{t('bulk.undo.accepted')}</span>
        </div>
      )}

      {runs.isPending && <SkeletonRows rows={4} columns={7} />}

      {runs.isError && (
        <Alert tone="error" title={t('bulk.error.title')}>
          {runs.error.message}
        </Alert>
      )}

      {runs.isSuccess && items.length === 0 && (
        <div className="lq-cp__none">
          <div className="lq-cp__noneTitle">{t('bulk.empty.title')}</div>
          <div className="lq-cp__noneBody">{t('bulk.empty.guidance')}</div>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="lq-table-scroll">
            <table className="lq-table lq-table--ledger">
              <caption className="lq-bk__caption">{t('bulk.caption')}</caption>
              <thead>
                <tr>
                  <th>{t('bulk.column.action')}</th>
                  <th className="lq-table__num">{t('bulk.column.items')}</th>
                  <th className="lq-table__num">{t('bulk.column.chunk')}</th>
                  <th>{t('bulk.column.state')}</th>
                  <th>{t('bulk.column.by')}</th>
                  <th>{t('bulk.column.undo')}</th>
                  <th aria-label={t('bulk.column.report')} />
                </tr>
              </thead>
              <tbody>
                {items.map((run) => (
                  <RunRow
                    key={run.run_id}
                    run={run}
                    selected={run.run_id === selected}
                    onSelect={() => {
                      setSelected(run.run_id);
                      undo.reset();
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* `/v1/rules/bulk` is `ORDER BY requested_at DESC LIMIT 50` with no cursor — S-02
              records that gap. Saying so is the honest half a screen can do without the backend
              change. */}
          {items.length >= 50 && <p className="lq-bk__note">{t('bulk.truncated')}</p>}
          <p className="lq-bk__note">{t('bulk.partialNote')}</p>
        </>
      )}

      {current !== null && (
        <RunReport
          run={current}
          report={report}
          undo={undo}
          selectedId={current.run_id}
        />
      )}
    </div>
  );
}

function RunRow({
  run,
  selected,
  onSelect,
}: {
  readonly run: BulkRun;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const standing = revertStanding(run);

  return (
    <tr
      onClick={onSelect}
      className={
        run.state === 'partial' ? 'lq-bk__row lq-bk__row--partial' : 'lq-bk__row'
      }
    >
      <td>
        {t(`bulk.action.${actionLabel(run)}`)}
        {/* A run has no name — the id is its only identity — but the full 36-character UUID as the
            secondary line is exactly the raw-id-as-primary-label defect: shortened, full value on
            hover, same shape as the incident queue's `inc-${id.slice(0, 8)}`. */}
        <div className="lq-bk__runId" title={run.run_id}>
          {t('bulk.run.shortId', { id: run.run_id.slice(0, 8) })}
        </div>
      </td>
      <td className="lq-table__num">{run.item_count.toLocaleString()}</td>
      <td className="lq-table__num lq-bk__dim">{run.chunk_size.toLocaleString()}</td>
      <td>
        <span
          className={`lq-tag lq-tag--bk-${STATE_TONE[run.state]}`}
          title={t(`bulk.stateHint.${run.state}`)}
        >
          <span className="lq-tag__dot" aria-hidden="true" />
          {t(`bulk.state.${run.state}`)}
        </span>
      </td>
      <td className="lq-bk__who">{run.requested_by}</td>
      <td>
        {/* Five standings, not a boolean — `undoable: false` covers four different facts, and a
            run that *was* revertable an hour ago and is not now looks like a bug unless the cell
            says which fact applies. */}
        <span className={standing === 'available' ? 'lq-bk__undoOk' : 'lq-bk__undoNo'}>
          {t(`bulk.standing.${standing}`)}
        </span>
        <div className="lq-bk__undoNote">
          {standing === 'available' &&
            (run.undo_expires_at !== null && run.undo_expires_at !== undefined
              ? t('bulk.undo.until', { when: run.undo_expires_at })
              : t('bulk.undo.noWindow'))}
        </div>
      </td>
      <td className="lq-bk__select">
        <button type="button" className="lq-bk__selectLink" onClick={onSelect}>
          {selected ? t('bulk.select.selected') : t('bulk.select.show')}
        </button>
      </td>
    </tr>
  );
}

function RunReport({
  run,
  report,
  undo,
  selectedId,
}: {
  readonly run: BulkRun;
  readonly report: ReturnType<typeof useBulkReport>;
  readonly undo: ReturnType<typeof useUndoBulkRun>;
  readonly selectedId: string;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <>
      <h3 className="lq-bk__reportTitle">
        {t('bulk.report.title', { action: t(`bulk.action.${actionLabel(run)}`) })}
      </h3>
      {/* The journal for a run that has one, the dry-run for one that does not — and the hint
          says which, because "read from the journal" over a preview is the exact conflation this
          split exists to end. */}
      <p className="lq-bk__reportHint">
        {reportSource(run.state) === 'report'
          ? t('bulk.report.hint')
          : t('bulk.report.previewHint')}
      </p>

      {report.isPending && <p role="status">{t('bulk.report.loading')}</p>}
      {report.isError && <Alert tone="error">{report.error.message}</Alert>}

      {report.isSuccess && report.data !== null && (
        <>
          {/* Every status including the zeroes, in the reference's own frame — the same
              `lq-runstats` the executions screen uses, because these four are one reading. */}
          <div className="lq-runstats lq-bk__counts">
            {countsOf(report.data).map((entry) => (
              <div key={entry.status} className="lq-runstat">
                <span className="lq-runstat__label">
                  {t(`bulk.status.${entry.status}`)}
                </span>
                <span
                  className={
                    entry.benign || entry.count === 0
                      ? entry.benign
                        ? 'lq-runstat__value'
                        : 'lq-runstat__value lq-bk__zero'
                      : 'lq-runstat__value lq-runstat__value--danger'
                  }
                >
                  {entry.count.toLocaleString()}
                </span>
                <span className="lq-bk__countNote">
                  {t(`bulk.statusNote.${entry.status}`)}
                </span>
              </div>
            ))}
          </div>

          {!accountsForEveryItem(report.data) && (
            <Alert tone="warning" title={t('bulk.unaccounted.title')}>
              {t('bulk.unaccounted.body', {
                counted: countsOf(report.data).reduce((sum, e) => sum + e.count, 0),
                total: report.data.total_items,
              })}
            </Alert>
          )}

          <div className="lq-bk__pair">
            <div className="lq-bk__panel">
              <div className="lq-bk__panelTitle">{t('bulk.chunks.title')}</div>
              {chunkRows(run, report.data).map((chunk) => (
                <ChunkBar key={chunk.label} chunk={chunk} />
              ))}
              <p className="lq-bk__note">
                {t('bulk.report.phaseNote', { phase: report.data.phase })}
              </p>
            </div>

            <RevertPanel
              run={run}
              data={report.data}
              undo={undo}
              selectedId={selectedId}
            />
          </div>
        </>
      )}
    </>
  );
}

function ChunkBar({ chunk }: { readonly chunk: ChunkRow }): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="lq-bk__chunk">
      <div className="lq-bk__chunkLabel">{chunk.label}</div>
      {/* The bar carries no number: a chunk is committed whole or not at all, so its states are
          full, empty, and the deliberately indeterminate partial fill of the one being written. */}
      <div className="lq-bk__chunkTrack">
        <div className={`lq-bk__chunkFill lq-bk__chunkFill--${chunk.verdict}`} />
      </div>
      <div
        className={`lq-bk__chunkVerdict lq-bk__chunkVerdict--${chunk.verdict}`}
        title={chunk.error ?? undefined}
      >
        {t(`bulk.chunk.${chunk.verdict}`)}
      </div>
    </div>
  );
}

function RevertPanel({
  run,
  data,
  undo,
  selectedId,
}: {
  readonly run: BulkRun;
  readonly data: BulkReport;
  readonly undo: ReturnType<typeof useUndoBulkRun>;
  readonly selectedId: string;
}): ReactElement {
  const { t } = useTranslation();
  const standing = revertStanding(run);
  const reach = undoReach(data);

  return (
    <div className="lq-bk__panel">
      <div className="lq-bk__panelTitle">{t('bulk.revert.title')}</div>

      {standing === 'available' ? (
        <>
          {/* The number comes from `applied`, never from `total_items`. This is the whole screen:
              promising 412 when 374 will move is the misleading version. */}
          <p className="lq-bk__revertExplain">
            {t('bulk.undo.explain', { reach, total: data.total_items })}
          </p>
          <p className="lq-bk__note">
            {run.undo_expires_at !== null && run.undo_expires_at !== undefined
              ? t('bulk.undo.window', { when: run.undo_expires_at })
              : t('bulk.undo.windowOpen')}
          </p>
          <button
            type="button"
            className="lq-bk__revert"
            disabled={undo.isPending}
            onClick={() => {
              undo.mutate(selectedId);
            }}
          >
            {t('bulk.undo.action', { reach })}
          </button>
          {undo.isError && (
            <Alert tone="error" title={t('bulk.undo.failed')}>
              {undo.error.message}
            </Alert>
          )}
        </>
      ) : (
        <>
          <p className="lq-bk__revertExplain">{t(`bulk.blocked.${standing}`)}</p>
          <span className="lq-bk__revertOff" title={t(`bulk.blocked.${standing}`)}>
            {standing === 'in_flight' ? t('bulk.undo.notYet') : t('bulk.undo.closed')}
          </span>
        </>
      )}

      {undo.isSuccess && <div className="lq-bk__accepted">{t('bulk.undo.accepted')}</div>}
    </div>
  );
}
